import { FetchArgs } from '@reduxjs/toolkit/query'

import { StoredAfterWrite } from '../features/plans/types/Plans'
import {
  InternalOrganizationAccessChange,
  InternalOrganizationAccessGrantAll,
  InternalOrganizationPermissions,
  InternalPermission,
  InternalPermissionChange,
  InternalPermissionType,
} from '../types/InternalPermission'
import { jsonAnswer, optionalJson } from './helpers'
import { internalApiserverApi } from './internal-apiserver'

// Both directions answer the item's row after the change, are idempotent and leave the item's other organizations untouched.
const changeRequest = ({ type, itemId, organizationIds }: InternalPermissionChange, granted: boolean): FetchArgs => ({
  url: `i/permissions/${granted ? 'add' : 'remove'}/${itemId}`,
  method: 'POST',
  params: { type },
  body: { organizationIds },
  headers: jsonAnswer,
  responseHandler: optionalJson,
})

const organizationPermissions = (organizationId: string) => `i/permissions/single/organization/${organizationId}`
const organizationTags = (organizationIds: string[]) => organizationIds.map((id) => ({ type: 'internalOrganizationPermissions' as const, id }))
const itemListTag = (type: InternalPermissionType) => ({ type: 'internalPermissions' as const, id: type })

const permissionsApi = internalApiserverApi.enhanceEndpoints({ addTagTypes: ['internalPermissions', 'internalOrganizationPermissions'] }).injectEndpoints({
  endpoints: (builder) => ({
    getInternalPermissions: builder.query<InternalPermission[], InternalPermissionType>({
      query: (type) => ({ url: 'i/permissions/list/', params: { type } }),
      providesTags: (_result, _error, type) => [itemListTag(type)],
    }),
    // Not the item list: `OrganizationAccessDialog` reads its change back with its own refetch.
    addInternalPermissions: builder.mutation<InternalPermission | null, InternalPermissionChange>({
      query: (change) => changeRequest(change, true),
      invalidatesTags: (_result, _error, { organizationIds }) => organizationTags(organizationIds),
    }),
    removeInternalPermissions: builder.mutation<InternalPermission | null, InternalPermissionChange>({
      query: (change) => changeRequest(change, false),
      invalidatesTags: (_result, _error, { organizationIds }) => organizationTags(organizationIds),
    }),
    /** Answers 400 for an organization that doesn't exist. */
    getInternalOrganizationPermissions: builder.query<InternalOrganizationPermissions, string>({
      query: organizationPermissions,
      providesTags: (_result, _error, organizationId) => organizationTags([organizationId]),
    }),
    setInternalOrganizationAccess: builder.mutation<StoredAfterWrite<InternalOrganizationPermissions>, InternalOrganizationAccessChange>({
      async queryFn({ organizationId, type, itemId, granted }, _api, _extraOptions, baseQuery) {
        const saved = await baseQuery(changeRequest({ type, itemId, organizationIds: [organizationId] }, granted))
        if (saved.error) return { error: saved.error }
        const stored = await baseQuery(organizationPermissions(organizationId))
        if (stored.error) return { error: stored.error }
        return { data: { stored: stored.data as InternalOrganizationPermissions } }
      },
      invalidatesTags: (_result, _error, { organizationId, type }) => [...organizationTags([organizationId]), itemListTag(type)],
    }),
    // Answers the organization's access, de-duplicated.
    grantInternalOrganizationAccessToAll: builder.mutation<StoredAfterWrite<InternalOrganizationPermissions>, InternalOrganizationAccessGrantAll>({
      async queryFn({ organizationId, type, itemIds }, _api, _extraOptions, baseQuery) {
        const saved = await baseQuery({
          url: `i/permissions/add/organization/${organizationId}`,
          method: 'POST',
          params: { type },
          body: { itemIds },
          headers: jsonAnswer,
          responseHandler: optionalJson,
        })
        if (saved.error) return { error: saved.error }
        const stored = await baseQuery(organizationPermissions(organizationId))
        if (stored.error) return { error: stored.error }
        return { data: { stored: stored.data as InternalOrganizationPermissions } }
      },
      invalidatesTags: (_result, _error, { organizationId, type }) => [...organizationTags([organizationId]), itemListTag(type)],
    }),
  }),
})

export const {
  useGetInternalPermissionsQuery,
  useLazyGetInternalPermissionsQuery,
  useAddInternalPermissionsMutation,
  useRemoveInternalPermissionsMutation,
  useGetInternalOrganizationPermissionsQuery,
  useSetInternalOrganizationAccessMutation,
  useGrantInternalOrganizationAccessToAllMutation,
} = permissionsApi
